import {graphql, useFragment} from 'react-relay'

import type {
  IssueEventConfidenceLevel,
  IssueStateReason,
  IssueTypeColor,
  PendingIssueSuggestionState,
  useRecentAgentActivity_issue$data,
  useRecentAgentActivity_issue$key,
} from './__generated__/useRecentAgentActivity_issue.graphql'

export type AgentActionType = 'label_added' | 'type_set' | 'field_set' | 'issue_closed' | 'assignee_assigned'

export type PendingSuggestionState = PendingIssueSuggestionState

export type SuggestionConfidence = 'HIGH' | 'MEDIUM' | 'LOW'

export type CloseStateReason = 'COMPLETED' | 'NOT_PLANNED' | 'DUPLICATE'

export type DuplicateTarget = {
  number: number
  title: string
  url: string
  repoNameWithOwner: string
}

export type PendingSuggestion = {
  type: AgentActionType
  value: string
  color: string | null
  state: PendingSuggestionState
  confidence: SuggestionConfidence | null
  intentId: string | null
  actorId: string
  actorTypename: string
  actorLogin: string
  actorAvatarUrl: string
  actorDisplayName: string | null
  rationale: string | null
  // Used as the label shown on the row (e.g. "Type", "Label", or a field name)
  displayFieldName: string
  // Type suggestions only — used to wire up apply/reject mutations.
  issueTypeId: string | null
  issueTypeName: string | null
  issueTypeColor: IssueTypeColor | null
  // Label suggestions only.
  labelId: string | null
  // Field suggestions only.
  issueFieldId: string | null
  // Close suggestions only.
  closeStateReason: CloseStateReason | null
  duplicateOf: DuplicateTarget | null
  // Assignee suggestions only.
  assigneeId: string | null
  // Login of the suggested assignee. Null for non-assignee suggestions.
  assigneeLogin: string | null
  // Whether the suggested assignee is a coding-agent bot. False for non-assignee suggestions.
  assigneeIsAgent: boolean
  // Avatar URL of the suggested assignee (user or bot/agent). Null for non-assignee suggestions.
  avatarUrl: string | null
}

export type AgentSuggestionsGroup = {
  agentName: string
  agentSlug: string
  actorLogin: string
  actorAvatarUrl: string
  suggestions: PendingSuggestion[]
}

export type AgentSuggestionsResult = {
  hasPendingSuggestions: boolean
  groups: AgentSuggestionsGroup[]
  totalSuggestionCount: number
}

export type RecentAgentActivityResult = {
  issueId: string
  viewerCanUpdateMetadata: boolean
  repoNameWithOwner: string
  pendingSuggestions: AgentSuggestionsResult
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const recentAgentActivityFragment = graphql`
  fragment useRecentAgentActivity_issue on Issue {
    id
    viewerCanUpdateMetadata
    repository {
      nameWithOwner
    }
    pendingSuggestions {
      __typename
      ... on PendingTypeSuggestion {
        typeState: state
        intent {
          intentId
        }
        actor {
          __typename
          login
          avatarUrl
          id
          ... on Bot {
            displayName
          }
        }
        confidence
        rationale
        issueType {
          id
          name
          color
        }
      }
      ... on PendingLabelSuggestion {
        labelState: state
        intent {
          intentId
        }
        actor {
          __typename
          login
          avatarUrl
          id
          ... on Bot {
            displayName
          }
        }
        confidence
        rationale
        label {
          id
          name
          color
        }
      }
      ... on PendingFieldSuggestion {
        fieldState: state
        intent {
          intentId
        }
        actor {
          __typename
          login
          avatarUrl
          id
          ... on Bot {
            displayName
          }
        }
        rationale
        suggestedValue
        confidence
        issueField {
          ... on Node {
            id
          }
          ... on IssueFieldCommon {
            name
          }
          ... on IssueFieldSingleSelect {
            options {
              name
              color
            }
          }
        }
      }
      ... on PendingCloseSuggestion {
        closeState: state
        intent {
          intentId
        }
        actor {
          __typename
          login
          avatarUrl
          id
          ... on Bot {
            displayName
          }
        }
        rationale
        confidence
        stateReason
        duplicateOf {
          __typename
          ... on Issue {
            number
            title
            url
            repository {
              nameWithOwner
            }
          }
          ... on PullRequest {
            number
            title
            url
            repository {
              nameWithOwner
            }
          }
        }
      }
      ... on PendingAssigneeSuggestion {
        assigneeState: state
        intent {
          intentId
        }
        actor {
          __typename
          login
          avatarUrl
          id
          ... on Bot {
            displayName
          }
        }
        confidence
        rationale
        assignee {
          __typename
          ... on Node {
            id
          }
          ... on Actor {
            login
            displayName
            avatarUrl
          }
          ... on Bot {
            isAgent
          }
        }
      }
    }
  }
`

function normalizeConfidence(value: IssueEventConfidenceLevel | null | undefined): SuggestionConfidence | null {
  if (value === 'HIGH' || value === 'MEDIUM' || value === 'LOW') return value
  return null
}

function normalizeCloseStateReason(value: IssueStateReason | null | undefined): CloseStateReason | null {
  if (value === 'COMPLETED' || value === 'NOT_PLANNED' || value === 'DUPLICATE') return value
  return null
}

function closeStateReasonLabel(reason: CloseStateReason): string {
  switch (reason) {
    case 'COMPLETED':
      return 'completed'
    case 'NOT_PLANNED':
      return 'not planned'
    case 'DUPLICATE':
      return 'duplicate'
  }
}

type RawPendingSuggestion = NonNullable<useRecentAgentActivity_issue$data['pendingSuggestions']>[number]

type RenderablePendingSuggestion = Exclude<RawPendingSuggestion, {__typename: '%other'}>

function isRenderablePendingSuggestion(suggestion: RawPendingSuggestion): suggestion is RenderablePendingSuggestion {
  return suggestion.__typename !== '%other'
}

// Each suggestion type stores its review state under a differently-aliased field.
function suggestionStateFor(suggestion: RenderablePendingSuggestion): PendingSuggestionState {
  switch (suggestion.__typename) {
    case 'PendingTypeSuggestion':
      return suggestion.typeState
    case 'PendingLabelSuggestion':
      return suggestion.labelState
    case 'PendingFieldSuggestion':
      return suggestion.fieldState
    case 'PendingCloseSuggestion':
      return suggestion.closeState
    case 'PendingAssigneeSuggestion':
      return suggestion.assigneeState
  }
}

type RawDuplicateTarget = NonNullable<
  Extract<RawPendingSuggestion, {__typename: 'PendingCloseSuggestion'}>['duplicateOf']
>

function extractDuplicateTarget(target: RawDuplicateTarget | null | undefined): DuplicateTarget | null {
  if (!target) return null
  if (target.__typename !== 'Issue' && target.__typename !== 'PullRequest') return null
  if (typeof target.number !== 'number' || !target.url || !target.title || !target.repository?.nameWithOwner) {
    return null
  }
  return {
    number: target.number,
    title: target.title,
    url: target.url,
    repoNameWithOwner: target.repository.nameWithOwner,
  }
}

// Type-discriminated fields default to empty; each branch below overrides only
// the ones relevant to its suggestion type. Spreading this keeps every pushed
// row a complete PendingSuggestion without repeating the null-fill per branch.
const EMPTY_SUGGESTION_FIELDS = {
  issueTypeId: null,
  issueTypeName: null,
  issueTypeColor: null,
  labelId: null,
  issueFieldId: null,
  closeStateReason: null,
  duplicateOf: null,
  assigneeId: null,
  assigneeLogin: null,
  assigneeIsAgent: false,
  avatarUrl: null,
} as const

export function processPendingSuggestions(
  suggestions: useRecentAgentActivity_issue$data['pendingSuggestions'],
): AgentSuggestionsResult {
  const groupsBySlug = new Map<string, AgentSuggestionsGroup>()
  let totalSuggestionCount = 0

  if (!suggestions) {
    return {
      hasPendingSuggestions: false,
      groups: [],
      totalSuggestionCount: 0,
    }
  }

  for (const suggestion of suggestions) {
    if (!suggestion || !isRenderablePendingSuggestion(suggestion)) continue
    if (!suggestion.actor) continue

    // The server returns suggestions in all states (PENDING / APPLIED / REJECTED).
    // The panel only renders PENDING. Transient APPLIED chips shown immediately
    // after a viewer approves are tracked locally in the component instead.
    if (suggestionStateFor(suggestion) !== 'PENDING') continue

    const actor = suggestion.actor
    const actorDisplayName = actor.__typename === 'Bot' ? (actor.displayName ?? null) : null

    let group = groupsBySlug.get(actor.login)
    if (!group) {
      group = {
        agentName: actor.login,
        agentSlug: actor.login,
        actorLogin: actor.login,
        actorAvatarUrl: actor.avatarUrl,
        suggestions: [],
      }
      groupsBySlug.set(actor.login, group)
    }

    const baseSuggestion = {
      actorId: actor.id,
      actorTypename: actor.__typename,
      actorLogin: actor.login,
      actorAvatarUrl: actor.avatarUrl,
      actorDisplayName,
      rationale: suggestion.rationale ?? null,
      confidence: normalizeConfidence(suggestion.confidence),
      intentId: suggestion.intent?.intentId ?? null,
    }

    if (suggestion.__typename === 'PendingTypeSuggestion' && suggestion.issueType) {
      const issueType = suggestion.issueType
      group.suggestions.push({
        ...baseSuggestion,
        ...EMPTY_SUGGESTION_FIELDS,
        type: 'type_set',
        state: suggestion.typeState ?? 'PENDING',
        displayFieldName: 'Type',
        value: issueType.name,
        color: issueType.color ?? null,
        issueTypeId: issueType.id,
        issueTypeName: issueType.name,
        issueTypeColor: issueType.color ?? null,
      })
      totalSuggestionCount++
    } else if (suggestion.__typename === 'PendingLabelSuggestion' && suggestion.label) {
      const label = suggestion.label
      group.suggestions.push({
        ...baseSuggestion,
        ...EMPTY_SUGGESTION_FIELDS,
        type: 'label_added',
        state: suggestion.labelState ?? 'PENDING',
        displayFieldName: 'Label',
        value: label.name,
        color: label.color ?? null,
        labelId: label.id,
      })
      totalSuggestionCount++
    } else if (suggestion.__typename === 'PendingFieldSuggestion' && suggestion.issueField) {
      // Skip field suggestions with no suggested value — there's nothing to render.
      if (!suggestion.suggestedValue) continue
      const issueField = suggestion.issueField
      const matchingOption = issueField.options
        ? issueField.options.find(o => o.name === suggestion.suggestedValue)
        : undefined
      group.suggestions.push({
        ...baseSuggestion,
        ...EMPTY_SUGGESTION_FIELDS,
        type: 'field_set',
        state: suggestion.fieldState ?? 'PENDING',
        displayFieldName: issueField.name ?? 'Field',
        value: suggestion.suggestedValue,
        color: matchingOption?.color ?? null,
        issueFieldId: issueField.id ?? null,
      })
      totalSuggestionCount++
    } else if (suggestion.__typename === 'PendingCloseSuggestion') {
      const closeStateReason = normalizeCloseStateReason(suggestion.stateReason)
      // Skip closes whose reason we don't render (e.g. REOPENED, or null/unknown).
      if (!closeStateReason) continue
      const duplicateOf = extractDuplicateTarget(suggestion.duplicateOf)
      // A DUPLICATE suggestion without a target issue is incoherent — skip it
      // rather than render an empty chip.
      if (closeStateReason === 'DUPLICATE' && !duplicateOf) continue
      group.suggestions.push({
        ...baseSuggestion,
        ...EMPTY_SUGGESTION_FIELDS,
        type: 'issue_closed',
        state: suggestion.closeState ?? 'PENDING',
        displayFieldName: 'Close',
        value: closeStateReasonLabel(closeStateReason),
        color: null,
        closeStateReason,
        duplicateOf,
      })
      totalSuggestionCount++
    } else if (suggestion.__typename === 'PendingAssigneeSuggestion' && suggestion.assignee) {
      const assignee = suggestion.assignee
      const assigneeValue = assignee.displayName ?? assignee.login ?? null
      // Skip assignee suggestions we can't name — there's nothing to render.
      if (!assigneeValue) continue
      group.suggestions.push({
        ...baseSuggestion,
        ...EMPTY_SUGGESTION_FIELDS,
        type: 'assignee_assigned',
        state: suggestion.assigneeState ?? 'PENDING',
        displayFieldName: 'Assignee',
        value: assigneeValue,
        color: null,
        assigneeId: assignee.id ?? null,
        assigneeLogin: assignee.login ?? null,
        assigneeIsAgent: assignee.__typename === 'Bot' && assignee.isAgent === true,
        avatarUrl: assignee.avatarUrl ?? null,
      })
      totalSuggestionCount++
    }
  }

  const groups = Array.from(groupsBySlug.values()).filter(group => group.suggestions.length > 0)
  groups.sort((a, b) => a.agentSlug.localeCompare(b.agentSlug))

  return {
    hasPendingSuggestions: totalSuggestionCount > 0,
    groups,
    totalSuggestionCount,
  }
}

export function useRecentAgentActivity(issueRef: useRecentAgentActivity_issue$key): RecentAgentActivityResult {
  const data = useFragment(recentAgentActivityFragment, issueRef)
  const pendingSuggestions = processPendingSuggestions(data.pendingSuggestions ?? [])
  return {
    issueId: data.id,
    viewerCanUpdateMetadata: data.viewerCanUpdateMetadata ?? false,
    repoNameWithOwner: data.repository.nameWithOwner,
    pendingSuggestions,
  }
}
